import type { FunctionalComponent } from 'preact'

interface NonProspectSwitchProps {
  showNonProspects: boolean
  onToggle: () => void
  /** How many rows in scope are non-prospects, so the switch can say what it would reveal. */
  nonProspectCount?: number
  class?: string
}

/**
 * Reveals people on a non-prospect domain — our own staff, the customer's, or an event's guest
 * co-host. Showing them is a MODE, not an addition: it shows only those people.
 *
 * Extracted from AudienceViewToggle (ORB-623) so the company profile can offer the same control
 * without the audience/non-audience axis, which has nothing to slice on a page where every person
 * is someone this workspace already targeted or heard from. One implementation, so the surfaces
 * cannot drift in what the switch means or looks like.
 */
export const NonProspectSwitch: FunctionalComponent<NonProspectSwitchProps> = ({
  showNonProspects,
  onToggle,
  nonProspectCount,
  class: className = '',
}) => {
  const hasNonProspects = nonProspectCount === undefined || nonProspectCount > 0

  return (
    <div class={`inline-flex items-center gap-1 text-[11px] font-medium text-gray-600 dark:text-gray-300 ${className}`.trim()}>
      <button
        type="button"
        onClick={onToggle}
        disabled={!hasNonProspects}
        aria-pressed={showNonProspects}
        title={
          hasNonProspects
            ? 'Show people on a non-prospect domain — your own staff, the customer’s, or an event guest co-host'
            : 'No non-prospects in this view'
        }
        class={`relative inline-flex items-center h-5 w-10 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-green-500 dark:focus:ring-offset-slate-800 disabled:opacity-40 ${
          showNonProspects ? 'bg-green-600' : 'bg-slate-300 dark:bg-slate-600'
        }`}
      >
        <span class="sr-only">Show non-prospects</span>
        <span
          class={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transform transition-transform ${
            showNonProspects ? 'translate-x-5' : ''
          }`}
        />
      </button>
      <span class="whitespace-nowrap hidden sm:inline">
        Show non-prospects{nonProspectCount !== undefined && nonProspectCount > 0 ? ` (${nonProspectCount})` : ''}
      </span>
      <span class="sm:hidden">Non-prosp.</span>
    </div>
  )
}

export default NonProspectSwitch
